import { Badge, Button, Pagination, Stack, Table, Text } from "@bookingcom/bui-react";
import { EditIcon } from "@bookingcom/bui-assets-react/streamline";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { PortalUser } from "../../types";
import { UserManagementEmptyState } from "./UserManagementEmptyState";
import styles from "./UserManagementTable.module.css";

type UserManagementTableProps = {
  users: PortalUser[];
  page: number;
  totalCount: number;
  size: number;
  onPageChange: (page: number) => void;
  onEdit?: (user: PortalUser) => void;
  isUpdating?: boolean;
};

export const UserManagementTable = ({
  users,
  page,
  totalCount,
  size,
  onPageChange,
  onEdit,
  isUpdating,
}: UserManagementTableProps) => {
  const i18n = useI18n();
  const totalPages = Math.ceil(totalCount / size);

  if (users.length === 0) {
    return <UserManagementEmptyState />;
  }

  return (
    <Stack gap={0} className={isUpdating ? styles.updating : undefined} attributes={{ "aria-busy": isUpdating ? "true" : undefined }}>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Heading>{i18n.trans(t("flights_airline_portal_user_management_list_email_header"))}</Table.Heading>
            <Table.Heading>{i18n.trans(t("flights_airline_portal_user_management_list_airlines_header"))}</Table.Heading>
            <Table.Heading width="120px">{i18n.trans(t("flights_airline_portal_user_management_list_edit_header"))}</Table.Heading>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {users.map((user) => (
            <Table.Row key={user.auth0UserId} verticalAlign="center">
              <Table.Cell>
                <Text variant="body_2">{user.email}</Text>
              </Table.Cell>
              <Table.Cell>
                {user.airlineCodes.length === 0 ? (
                  <Badge variant="destructive" alternative text={i18n.trans(t("flights_airline_portal_user_management_list_airlines_no_access"))} />
                ) : user.airlineCodes.length === 1 ? (
                  <Badge alternative text={user.airlineCodes[0]} />
                ) : (
                  <Stack gap={1}>
                    {user.groupName && <Text variant="body_2">{user.groupName}</Text>}
                    <Stack direction="row" gap={1}>
                      {user.airlineCodes.map((code) => (
                        <Badge key={code} alternative text={code} />
                      ))}
                    </Stack>
                  </Stack>
                )}
              </Table.Cell>
              <Table.Cell align="center">
                <Button
                  variant="tertiary"
                  icon={EditIcon}
                  size="medium"
                  attributes={{ "aria-label": `Edit ${user.email}` }}
                  onClick={() => onEdit?.(user)}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {totalPages > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          ariaPageLabelPrefix="Page"
          ariaPreviousLabel="Previous page"
          ariaNextLabel="Next page"
          onPageChange={({ page: newPage }) => onPageChange(newPage - 1)}
        />
      )}
    </Stack>
  );
};
